import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AGENT_STATUS_META } from "@/components/AgentStatusBadge";
import {
  CircleNotch, Users, Suitcase, ChartLineUp, Clock, Prohibit, Gift, ArrowUp,
} from "@phosphor-icons/react";

function fmtMoney(byCurrency) {
  if (!byCurrency || Object.keys(byCurrency).length === 0) return "$0";
  return Object.entries(byCurrency)
    .map(([cur, v]) => `${cur === "USD" ? "$" : ""}${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}${cur !== "USD" ? " " + cur : ""}`)
    .join(" · ");
}

export function AdminOverviewCards() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/overview");
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData({ error: true });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (data === null) {
    return (
      <div className="py-10 flex justify-center"><CircleNotch size={24} className="text-[#D4AF37] animate-spin" /></div>
    );
  }
  if (data?.error) return null;

  const heldCur = data.commissions?.held_by_currency || {};
  const blockedCur = data.commissions?.blocked_by_currency || {};
  const totalCur = data.commissions?.total_by_currency || {};
  const paidCur = data.commissions?.paid_by_currency || {};
  const byStatus = data.agents?.by_status || {};
  const bonusesOwed = data.referral_bonuses?.pending_total || 0;

  return (
    <div className="mb-8 animate-fade-up" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <Card
          icon={Users}
          label="Total agents"
          value={data.agents?.total || 0}
          sub={
            <span className="flex flex-wrap gap-1.5 mt-1">
              {(["active", "pending", "suspended", "inactive"]).map((s) => {
                const meta = AGENT_STATUS_META[s];
                const n = byStatus[s] || 0;
                if (!n) return null;
                return (
                  <span key={s} className={`inline-flex px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-widest border font-sans ${meta.cls}`}>
                    {n} {meta.label}
                  </span>
                );
              })}
              {Object.values(byStatus).every((v) => !v) && <span className="text-white/30 text-xs">No agents yet</span>}
            </span>
          }
          testId="overview-total-agents"
        />
        <Card
          icon={Suitcase}
          label="Total bookings"
          value={data.bookings?.total || 0}
          sub="Itineraries marked Booked"
          testId="overview-total-bookings"
        />
        <Card
          icon={ChartLineUp}
          label="Total commissions"
          value={fmtMoney(totalCur)}
          sub="All currencies, lifetime"
          gold
          testId="overview-total-commissions"
        />
        <Card
          icon={Clock}
          label="Held commissions"
          value={fmtMoney(heldCur)}
          sub={`${data.commissions?.row_counts?.held || 0} rows · pending agents`}
          tone="amber"
          testId="overview-held-commissions"
        />
        <Card
          icon={Prohibit}
          label="Blocked commissions"
          value={fmtMoney(blockedCur)}
          sub={`${data.commissions?.row_counts?.blocked || 0} rows · suspended`}
          tone="red"
          testId="overview-blocked-commissions"
        />
        <Card
          icon={Gift}
          label="Referral bonuses owed"
          value={`$${(bonusesOwed || 0).toLocaleString()}`}
          sub={`Paid out: $${(data.referral_bonuses?.paid_total || 0).toLocaleString()}`}
          gold
          testId="overview-bonuses-owed"
        />
      </div>
      {Object.keys(paidCur).length > 0 && (
        <p className="text-[11px] text-white/40 italic mt-3">
          Lifetime paid commissions: {fmtMoney(paidCur)}
        </p>
      )}
    </div>
  );
}

function Card({ icon: Icon, label, value, sub, gold, tone, testId }) {
  const wrapCls =
    tone === "amber"
      ? "border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-transparent"
      : tone === "red"
        ? "border-red-400/30 bg-gradient-to-br from-red-500/10 to-transparent"
        : gold
          ? "border-[#D4AF37]/35 bg-gradient-to-br from-[#D4AF37]/10 to-transparent"
          : "border-white/10 bg-ink-surface";
  const labelCls = tone === "amber" ? "text-amber-300" : tone === "red" ? "text-red-300" : gold ? "text-[#D4AF37]" : "text-white/50";
  return (
    <div className={`rounded-lg p-4 border ${wrapCls}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={13} weight="duotone" className={labelCls} />
        <p className={`text-[10px] uppercase tracking-[0.3em] font-sans ${labelCls}`}>{label}</p>
      </div>
      <p className="font-serif text-3xl text-white">{value}</p>
      <div className="text-xs text-white/55 font-sans mt-1">{sub}</div>
    </div>
  );
}
